// Thin fetch client for the secure-transfer API.
// Base URL defaults to api2.intake.dental in production; override via
// VITE_API_BASE=http://localhost:3000 for local dev.

const API_BASE = import.meta.env.VITE_API_BASE || 'https://api2.intake.dental';
const STORAGE_KEY = 'intake_send_session';

export function getSession() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}
export function setSession(s) { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); }
export function clearSession() { localStorage.removeItem(STORAGE_KEY); }

// ── Colleague referral ────────────────────────────────────────────────────
// A shared link lands as /?ref=st… . Stash it on first load so it survives the
// email round-trip (the code arrives in another tab, or another app entirely),
// and hand it over exactly once at verification.
const REF_KEY = 'intake_send_ref';
// Everything that is NOT a colleague code: ?src= (which surface sent them —
// listing, send_sent, forum_public…), ?listing= (the practice_listings id of
// the directory page they came from) and non-`st…` ref values such as
// `directory` / `claim` / `recipient-page`. Until 2026-10-28 these were read
// by analytics only and never reached the server, so a directory listing's
// "Send records" link produced signups the funnel could not see.
const ATTRIB_KEY = 'intake_send_attrib';
const SAFE = /^[a-z0-9_:-]{1,64}$/i;
const LISTING_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function captureReferralCode(search = window.location.search) {
  try {
    const q = new URLSearchParams(search);
    const ref = q.get('ref');
    if (ref && /^st[a-z0-9]{4,40}$/i.test(ref)) localStorage.setItem(REF_KEY, ref);
    const src = (q.get('src') || '').trim();
    const listing = (q.get('listing') || '').trim();
    const plainRef = ref && !/^st[a-z0-9]{4,40}$/i.test(ref) ? ref.trim() : '';
    const attrib = {
      ...(src && SAFE.test(src) ? { src } : {}),
      ...(listing && LISTING_RE.test(listing) ? { listing } : {}),
      ...(plainRef && SAFE.test(plainRef) ? { ref: plainRef } : {}),
    };
    // First touch wins within a visit: a later page that carries no params
    // must not wipe what the landing page recorded.
    if (Object.keys(attrib).length) {
      let prev = {};
      try { prev = JSON.parse(localStorage.getItem(ATTRIB_KEY) || '{}') || {}; } catch { prev = {}; }
      localStorage.setItem(ATTRIB_KEY, JSON.stringify({ ...attrib, ...prev }));
    }
  } catch { /* private mode — the invite just goes uncredited */ }
}

export function takeReferralCode() {
  try {
    const ref = localStorage.getItem(REF_KEY);
    if (ref) localStorage.removeItem(REF_KEY);
    return ref || undefined;
  } catch { return undefined; }
}

/**
 * Read-and-clear, like takeReferralCode: `{ ref, src, listing }` for the
 * verify-code body. `ref` keeps its exact old behaviour (the `st…` colleague
 * code, or nothing); `src` / `listing` come from the attribution slot. A
 * plain non-colleague ref (`directory`, `claim`) is reported as `src` when
 * no explicit src was captured, so `?ref=directory` finally counts.
 */
export function takeAttribution() {
  const ref = takeReferralCode();
  let attrib = {};
  try {
    attrib = JSON.parse(localStorage.getItem(ATTRIB_KEY) || '{}') || {};
    localStorage.removeItem(ATTRIB_KEY);
  } catch { attrib = {}; }
  return {
    ref,
    src: attrib.src || attrib.ref || undefined,
    listing: attrib.listing || undefined,
  };
}

/**
 * The marketing site's touchpoint tracker sets `intake_visitor_id` on
 * .intake.dental, so it's readable here. Passed in the body rather than left
 * to the browser — this app talks to api2.intake.dental cross-origin without
 * credentials, so the cookie itself wouldn't be sent. Read-only: never mints
 * an id, because a visitor with no tracked journey genuinely has none.
 */
export function readVisitorId() {
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith('intake_visitor_id='));
    return hit ? decodeURIComponent(hit.split('=').slice(1).join('=')).trim() || undefined : undefined;
  } catch { return undefined; }
}

async function request(path, { method = 'GET', body = null, auth = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const s = getSession();
    if (s?.token) headers.Authorization = `Bearer ${s.token}`;
  }
  const res = await fetch(`${API_BASE}/api/secure-transfer${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    // A rejected session is thrown away here, not left in localStorage.
    //
    // It used to stay. So a sender holding a token the API would not accept
    // — expired, or signed with a key the server no longer has after a
    // secret rotation — got an error on every action and no way back: the
    // app still believed it was signed in, so it never offered the sign-in
    // screen, and the only real fix was clearing site data. On 2026-09-19 a
    // rotation put every sender in exactly that state and sends stopped.
    //
    // Only on an authenticated call: a 401 from send-code or verify-code is
    // about the code they typed, and must not wipe a good session.
    if (res.status === 401 && auth) clearSession();
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    err.sessionCleared = res.status === 401 && auth;
    throw err;
  }
  return data;
}

// ── Auth ──────────────────────────────────────────────────────────────────
export const sendCode   = (email) => request('/auth/send-code', { method: 'POST', body: { email } });
export const verifyCode = ({ email, code, baa_accepted, marketing_opted_in, display_name }) => {
  // takeAttribution() is read-and-clear: a colleague's ref only ever credits
  // the first account it helps create, never every later sign-in. src /
  // listing ride along the same way (the backend records them on a NEW
  // sender only, via the referral arbiter). visitor_id ties a first-time
  // verify (i.e. a signup) back to the marketing journey; the backend
  // ignores it on subsequent logins.
  const { ref, src, listing } = takeAttribution();
  return request('/auth/verify-code', {
    method: 'POST',
    body: { email, code, baa_accepted, marketing_opted_in, display_name, ref, src, listing, visitor_id: readVisitorId() }
  });
};
export const getMe      = () => request('/me', { auth: true });
export const acceptBaa  = () => request('/me/baa', { method: 'POST', auth: true });
export const saveDisplayName = (display_name) => request('/me/profile', { method: 'POST', body: { display_name }, auth: true });
export const setMarketing = (opted_in) => request('/me/marketing', { method: 'POST', body: { opted_in }, auth: true });

// ── Share with a colleague ────────────────────────────────────────────────
// device:true mints a link + prefilled message for the sender's OWN messaging
// app; nothing is sent from our side. Emails we send ourselves.
export const inviteColleagues = (emails) =>
  request('/me/invite-colleague', { method: 'POST', body: { emails }, auth: true });
export const createDeviceShare = () =>
  request('/me/invite-colleague', { method: 'POST', body: { device: true }, auth: true });
export const listInvites = () => request('/me/invites', { auth: true });

// Recent recipients, derived from the sender's own transfer history — free
// tier: this is friction removal on the behaviour the growth loop needs.
export const listRecentRecipients = () => request('/me/recipients', { auth: true });

// ── Store pricing (public) ────────────────────────────────────────────────
// `free_gb.personal` / `free_gb.practice` are the only honest source for the
// "N GB free" copy; see hooks/useStoreFreeGb.js. Lives outside the
// /secure-transfer prefix, so it does not go through request().
export async function getStorePricing() {
  const res = await fetch(`${API_BASE}/api/store/pricing`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── Received (packages addressed to this sender's own email) ──────────────
// The API answers `{ received: [...], next_cursor, open_via }`. The delivery
// link cannot be rebuilt (the recipient token is stored hashed); a package is
// opened from here with openReceived() → /received/:recipientId#dt= instead.
export const listReceived = ({ limit = 25, cursor = null } = {}) => {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (cursor) qs.set('cursor', cursor);
  return request(`/me/received?${qs.toString()}`, { auth: true });
};

// Open a received package as its addressee: a 1h download token (`dt`)
// scoped to the recipient row, minted only when the signed-in email is the
// one the package was sent to. Errors carry the server's code: 403
// NOT_RECIPIENT, 410 GONE/EXPIRED. `e2e: true` means the files are
// end-to-end encrypted with the sender's PIN, which only the email link has.
export const openReceived = (recipientId) =>
  request(`/me/received/${encodeURIComponent(recipientId)}/open`, { method: 'POST', auth: true });
// What PIN verify returns after the PIN — the transfer and its files — for a
// package opened with that `dt`. Public route: the dt is the credential.
export const receivedPackage = (recipientId, dt) =>
  request(`/received/${encodeURIComponent(recipientId)}?dt=${encodeURIComponent(dt)}`);
export const receivedFileStreamUrl = (recipientId, fileId, dt, { download = false } = {}) =>
  `${API_BASE}/api/secure-transfer/received/${encodeURIComponent(recipientId)}/files/${fileId}/stream?dt=${encodeURIComponent(dt)}${download ? '&download=1' : ''}`;
export const receivedPackageUrl = (recipientId, dt) =>
  `${API_BASE}/api/secure-transfer/received/${encodeURIComponent(recipientId)}/package?dt=${encodeURIComponent(dt)}`;

// Keep a received package in the sender's own Store. Lives on the Store API
// (same host, /api/store), which accepts the Send Bearer token as the Store
// principal, so it bypasses the /secure-transfer prefix of request().
// Errors carry the server's code: 409 E2E_NOT_KEPT, 403 NOT_RECIPIENT, 410.
export async function claimReceived(recipient_id) {
  const s = getSession();
  const res = await fetch(`${API_BASE}/api/store/claim-received`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(s?.token ? { Authorization: `Bearer ${s.token}` } : {}) },
    body: JSON.stringify({ recipient_id }),
  });
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.code || null;
    err.data = data;
    throw err;
  }
  return data;
}

// Sent → Store without a second OTP: a 60 s launch code the Store app's
// /launch page exchanges for a session. Open `${url}?code=${code}`.
export const storeLaunchCode = () => request('/me/store-launch-code', { method: 'POST', auth: true });

// ── Quota + upgrade ───────────────────────────────────────────────────────
// The free tier is 5 sends a month, enforced server-side with a 402. These
// existed on the API with no caller — the paid tier was unbuyable from the
// product until this was wired.
export const getQuota = () => request('/me/quota', { auth: true });
export const startCheckout = () => request('/me/checkout', { method: 'POST', auth: true });

// ── Transfers ─────────────────────────────────────────────────────────────
export const createTransfer = (opts) => request('/transfers', { method: 'POST', body: opts, auth: true });
export const listTransfers  = () => request('/transfers', { auth: true });
export const revokeTransfer = (id, reason) => request(`/transfers/${id}/revoke`, { method: 'POST', body: { reason }, auth: true });
export const sendTransfer   = (id, body) => request(`/transfers/${id}/send`, { method: 'POST', body, auth: true });
// The code this sender used last time for each recipient ({ email: '1234' | null }).
export const lookupRecipientCodes = (emails) => request('/recipient-codes/lookup', { method: 'POST', body: { emails }, auth: true });
// Request-files reverse flow
export const createFileRequest  = (body) => request('/requests', { method: 'POST', body, auth: true });
export const resolveFileRequest = (token) => request(`/requests/resolve/${token}`);
// Send-it-back: who a return of the package behind this access token goes to.
export const getReturnContext = (token) => request(`/r/${encodeURIComponent(token)}/return-context`);

// ── File upload (S3 multipart via presigned URLs) ────────────────────────
export const initFileUpload = (transferId, file) =>
  request(`/transfers/${transferId}/files`, {
    method: 'POST', auth: true,
    body: {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size_bytes: file.size
    }
  });

export const getPartUrl = (transferId, fileId, partNumber) =>
  request(`/transfers/${transferId}/files/${fileId}/part`, {
    method: 'POST', auth: true,
    body: { part_number: partNumber }
  });

export const completeFile = (transferId, fileId, parts) =>
  request(`/transfers/${transferId}/files/${fileId}/complete`, {
    method: 'POST', auth: true,
    body: { parts }
  });

export const abortFile = (transferId, fileId) =>
  request(`/transfers/${transferId}/files/${fileId}/abort`, { method: 'POST', auth: true });

// Chunked upload orchestration — encrypts nothing client-side in v1; the
// bytes go straight to S3 via the presigned URL.
export async function uploadFile({ transferId, file, onProgress }) {
  const init = await initFileUpload(transferId, file);
  const { fileId, partSize, partsTotal } = init;
  const parts = [];
  let uploaded = 0;

  for (let i = 0; i < partsTotal; i++) {
    const start = i * partSize;
    const end   = Math.min(file.size, start + partSize);
    const blob  = file.slice(start, end);

    const { url } = await getPartUrl(transferId, fileId, i + 1);
    const put = await fetch(url, { method: 'PUT', body: blob });
    if (!put.ok) {
      await abortFile(transferId, fileId).catch(() => {});
      throw new Error(`Part ${i + 1} upload failed: ${put.status}`);
    }
    const etag = put.headers.get('ETag');
    parts.push({ PartNumber: i + 1, ETag: etag?.replace(/"/g, '') });

    uploaded += blob.size;
    if (onProgress) onProgress(uploaded, file.size);
  }

  await completeFile(transferId, fileId, parts);
  return { fileId };
}

// e2e variant: encrypts client-side while uploading — the server only ever
// receives framed AES-GCM ciphertext. Part count/sizes are computed from the
// exact ciphertext size so the standard multipart pipeline works unchanged.
export async function uploadFileE2E({ transferId, file, key, onProgress }) {
  const { ciphertextSize, encryptToParts } = await import('./utils/e2eCrypto.js');
  const ctSize = ciphertextSize(file.size);
  const init = await request(`/transfers/${transferId}/files`, {
    method: 'POST', auth: true,
    body: {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size_bytes: ctSize
    }
  });
  const { fileId, partSize, partsTotal } = init;
  const parts = [];
  let uploaded = 0;
  let partNumber = 0;
  for await (const blob of encryptToParts(file, key, partSize)) {
    partNumber += 1;
    const { url } = await getPartUrl(transferId, fileId, partNumber);
    const put = await fetch(url, { method: 'PUT', body: blob });
    if (!put.ok) {
      await abortFile(transferId, fileId).catch(() => {});
      throw new Error(`Part ${partNumber} upload failed: ${put.status}`);
    }
    parts.push({ PartNumber: partNumber, ETag: put.headers.get('ETag')?.replace(/"/g, '') });
    uploaded += blob.size;
    if (onProgress) onProgress(Math.min(uploaded, ctSize), ctSize);
  }
  if (partNumber !== partsTotal) {
    // sanity: server sized the multipart from our declared ciphertext size
    console.warn(`e2e parts mismatch: sent ${partNumber}, server expected ${partsTotal}`);
  }
  await completeFile(transferId, fileId, parts);
  await uploadVolumePreview({ transferId, fileId, file, key });
  return { fileId };
}

// A large DICOM volume also gets a small encrypted preview (16 slices, ~1–3 MB)
// so the recipient can scroll something within a second or two. Best effort:
// a failure here never fails the send — the recipient just waits for the file.
async function uploadVolumePreview({ transferId, fileId, file, key }) {
  try {
    const { makeVolumePreview } = await import('./utils/volumePreview.js');
    const preview = await makeVolumePreview(file);
    if (!preview) return;
    const { encryptToParts } = await import('./utils/e2eCrypto.js');
    const blobs = [];
    for await (const b of encryptToParts(new Blob([preview]), key, 64 * 1024 * 1024)) blobs.push(b);
    const s = getSession();
    const res = await fetch(`${API_BASE}/api/secure-transfer/transfers/${transferId}/files/${fileId}/preview`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream', ...(s?.token ? { Authorization: `Bearer ${s.token}` } : {}) },
      body: new Blob(blobs),
    });
    if (!res.ok) console.warn('volume preview upload failed', res.status);
  } catch (e) {
    console.warn('volume preview skipped', e);
  }
}

// The case thread behind a package: read the recent messages and reply.
export const getRecipientThread = (token, download_token) =>
  request(`/r/${token}/thread?dt=${encodeURIComponent(download_token)}`);
export const postRecipientReply = (token, download_token, body) =>
  request(`/r/${token}/reply`, { method: 'POST', body: { dt: download_token, body } });
// A package that carries a referral: its status, and the specialist's
// received / scheduled clicks.
export const getRecipientReferral = (token, download_token) =>
  request(`/r/${token}/referral?dt=${encodeURIComponent(download_token)}`);
export const postRecipientReferralStatus = (token, download_token, status, appointment_date) =>
  request(`/r/${token}/referral-status`, { method: 'POST', body: { dt: download_token, status, ...(appointment_date ? { appointment_date } : {}) } });

export const filePreviewUrl = (token, fileId, download_token) =>
  `${API_BASE}/api/secure-transfer/r/${token}/files/${fileId}/preview?dt=${encodeURIComponent(download_token)}`;

// ── Recipient (public, no auth) ──────────────────────────────────────────
export const resolveLink = (token) => request(`/r/${token}/resolve`, { method: 'POST' });
export const verifyPin   = (token, pin) => request(`/r/${token}/verify-pin`, { method: 'POST', body: { pin } });
// Prefers the short-lived download token from verify-pin; falls back to the
// legacy raw-PIN param only if the API didn't return one.
export const packageUrl  = (token, { download_token, pin } = {}) => download_token
  ? `${API_BASE}/api/secure-transfer/r/${token}/package?dt=${encodeURIComponent(download_token)}`
  : `${API_BASE}/api/secure-transfer/r/${token}/package?pin=${encodeURIComponent(pin)}`;
// Single-file stream — powers in-browser preview and per-file downloads
export const fileStreamUrl = (token, fileId, download_token, { download = false } = {}) =>
  `${API_BASE}/api/secure-transfer/r/${token}/files/${fileId}/stream?dt=${encodeURIComponent(download_token)}${download ? '&download=1' : ''}`;

// ── Send Spaces (case conversations with practices) ─────────────────────────
export const listSpaces       = () => request('/spaces', { auth: true });
export const listSpaceThreads = (spaceId) => request(`/spaces/${spaceId}/threads`, { auth: true });
export const getSpaceThread   = (spaceId, threadId) => request(`/spaces/${spaceId}/threads/${threadId}`, { auth: true });
export const postSpaceMessage = (spaceId, threadId, body) =>
  request(`/spaces/${spaceId}/threads/${threadId}/messages`, { method: 'POST', body: { body }, auth: true });
export const markSpaceRead    = (spaceId) => request(`/spaces/${spaceId}/read`, { method: 'POST', auth: true });
